import { gql } from '@apollo/client';

import { SelectedClients } from 'src/components/clientFilter/ClientFilter';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const ExcludedClientsClientFilterFragment = gql`
  fragment ExcludedClientsClientFilterFragment on ClientFilter {
    name
    version
  }
`;

export const ExcludedOperationNameFilterFragment = gql`
  fragment ExcludedOperationNameFilterFragment on OperationNameFilter {
    name
  }
`;

export type ExcludedClient = Omit<
  GraphQLTypes.ExcludedClientsClientFilterFragment,
  '__typename'
>;

export type ExcludedOperationName = Omit<
  GraphQLTypes.ExcludedOperationNameFilterFragment,
  '__typename'
>;

/**
 * Transform selectedClients into the format needed to save as a ClientFilter.
 * @param selectedClients as returned by a ClientFilter
 */
export function selectedClientsToExcludedClients(
  selectedClients: SelectedClients | null,
): GraphQLTypes.ClientFilterInput[] | null {
  if (selectedClients === null) {
    return [];
  }
  return Object.entries(selectedClients).flatMap(
    ([clientName, clientVersions]): GraphQLTypes.ClientFilterInput[] =>
      clientVersions === '*'
        ? [{ name: clientName, version: null }]
        : clientVersions.map((version) => ({
            name: clientName,
            version,
          })),
  );
}

/**
 * Transform CheckConfig.excludedClients into the format needed to display a ClientFilter.
 * @param excludedClients as returned from GraphQL
 */
export function excludedClientsToSelectedClients(
  excludedClients: ExcludedClient[] | null,
): SelectedClients | null {
  if (excludedClients === null) {
    return null;
  }
  return (
    excludedClients
      // TODO(tim): what would a null client name mean?  handle this when we
      // actually switch to referenceID here.
      .filter(
        (
          data,
        ): data is typeof data & { name: NonNullable<(typeof data)['name']> } =>
          data.name !== null,
      )
      .reduce((accumulator, { name, version }) => {
        const accumulatorValue = accumulator[name];
        if (accumulatorValue === '*') {
          // we've already decided to include all versions for this name,
          // do nothing
        } else if (name === '') {
          // translate unidentified clients into all or nothing
          accumulator[name] = '*';
        } else if (version === null) {
          // translate null versions into "all versions"
          accumulator[name] = '*';
        } else if (Array.isArray(accumulatorValue)) {
          // general case: build an array of version strings
          accumulatorValue.push(version);
        } else {
          // base case: initialize the array
          accumulator[name] = [version];
        }
        return accumulator;
      }, {} as SelectedClients)
  );
}

export function removeTypename(
  excludedClients: GraphQLTypes.ExcludedClientsClientFilterFragment[],
): ExcludedClient[] {
  return excludedClients.map((c) => ({ name: c.name, version: c.version }));
}

export function removeTypenameFromExcludedOperationNames(
  excludedOperationNames: (GraphQLTypes.OperationNameFilterInput | null)[],
): ExcludedOperationName[] {
  return (
    excludedOperationNames as GraphQLTypes.OperationNameFilterInput[]
  ).map(({ name }) => ({ name }));
}
